/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import Config from '@app/Config';
import RuntimeConfigStore from '@app/stores/RuntimeConfigStore';

type DownloadChannel = 'stable' | 'canary';
type DesktopPlatform = 'win32' | 'darwin' | 'linux';
type DesktopArch = 'x64' | 'ia32' | 'arm64';
type DesktopFormat = 'setup' | 'dmg' | 'deb';

function getDownloadChannel(): DownloadChannel {
	return Config.PUBLIC_RELEASE_CHANNEL === 'canary' ? 'canary' : 'stable';
}

function getDownloadApiEndpoint(): string {
	return RuntimeConfigStore.apiPublicEndpoint || Config.PUBLIC_BOOTSTRAP_API_PUBLIC_ENDPOINT || '/api';
}

function downloadApiUrl(path: string): string {
	const url = new URL(getDownloadApiEndpoint(), window.location.origin);
	const basePath = url.pathname.replace(/\/+$/u, '');
	const nextPath = path.startsWith('/') ? path : `/${path}`;
	url.pathname = `${basePath}${nextPath}`;
	return url.toString();
}

function detectDesktopPlatform(): DesktopPlatform {
	const platform = ((navigator as {userAgentData?: {platform?: string}}).userAgentData?.platform ?? navigator.platform)
		.toLowerCase()
		.trim();
	const userAgent = navigator.userAgent.toLowerCase();

	if (platform.includes('mac') || userAgent.includes('macintosh') || userAgent.includes('mac os x')) return 'darwin';
	if (platform.includes('linux') || (userAgent.includes('linux') && !userAgent.includes('android'))) return 'linux';
	return 'win32';
}

function detectDesktopArch(platform: DesktopPlatform): DesktopArch {
	const userAgent = navigator.userAgent.toLowerCase();

	if (platform === 'win32') {
		if (userAgent.includes('i386') || userAgent.includes('i686') || userAgent.includes('x86')) return 'ia32';
		return 'x64';
	}

	if (platform === 'darwin') {
		if (userAgent.includes('x86_64')) return 'x64';
		return 'arm64';
	}

	return userAgent.includes('arm64') || userAgent.includes('aarch64') || userAgent.includes('armv8') ? 'arm64' : 'x64';
}

function getDesktopFormat(platform: DesktopPlatform): DesktopFormat {
	if (platform === 'darwin') return 'dmg';
	if (platform === 'linux') return 'deb';
	return 'setup';
}

export function getDetectedDesktopDownloadUrl(): string {
	const platform = detectDesktopPlatform();
	const arch = detectDesktopArch(platform);
	const format = getDesktopFormat(platform);
	return downloadApiUrl(`/dl/desktop/${getDownloadChannel()}/${platform}/${arch}/latest/${format}`);
}

export function getDetectedMobileDownloadUrl(): string {
	const userAgent = navigator.userAgent.toLowerCase();
	if (userAgent.includes('android')) {
		return downloadApiUrl('/dl/android/arm64/apk');
	}
	return downloadApiUrl('/dl/ios/testflight');
}
